import moment from 'moment';
import { chain, cloneDeep, filter, get, isEqual, isFunction, orderBy, some } from 'lodash';

import {
  formatAttributeType,
  formatStatus,
  formatTimestamp,
  isUndeployed,
} from '@src/emp-3.0/utils/index';
import { EBlockedEventType, ESortOrder, EStatusStr } from '@src/emp-3.0/types';
import { isBlockAll, isBlockByRules, isDeployed } from '@src/emp-3.0/utils/checks';
import { sortByType } from '@src/common/constant/enum';

import {
  BLOCK_EVENTS,
  BLOCK_VIDEO_EVENTS,
  CUSTOM_DIMENSION,
  DEFAULT_SELECT_VALUE,
  DELETED,
  DEPLOYED,
  FLOW,
  Id_Key_MAPPING,
  MAPPED_EVENTS,
  METRICS,
  UNDEPLOYED,
} from '../constants/management';

export const createSetState = (set: (fn) => void, key: string) => (value) =>
  set(() => ({
    [key]: value,
  }));

export const removeInvalidSelectedList =
  (set: (fn) => void, key: string = 'selectedList') =>
  (value) =>
    set((state) => {
      const setOpenBulkAction = state.setOpenBulkAction;
      // Step 1: Filter selectedList based on valid IDs in allSelectedList
      const allValidIds = new Set(value.map((item) => item.id));
      const validSelectedList = state.selectedList.filter((item) => allValidIds.has(item.id));

      const validSelectedIds = new Set(validSelectedList.map((item) => item.id));
      const latestSelectedList = value.filter((item) => validSelectedIds.has(item.id));

      // Step 2: Compare updatedSelectedList and state.selectedList deeply
      const listsAreEqual = isEqual(
        state.selectedList.sort((a, b) => a.id.localeCompare(b.id)),
        latestSelectedList.sort((a, b) => a.id.localeCompare(b.id)),
      );

      // Step 3: Update state only if lists are different
      if (!listsAreEqual) {
        if (latestSelectedList.length === 0 && isFunction(setOpenBulkAction))
          setOpenBulkAction(false);
        return { [key]: latestSelectedList };
      }

      return state; // No state update
    });

interface ICreateFilterFunction {
  data: { versions: any[] };
  updateDate?: moment.Moment[] | null;
  deployDate?: moment.Moment[] | null;
  blockedDate?: moment.Moment[] | null;
  type?: EBlockedEventType;
  status?: EStatusStr;
  category?: string;
  createdBy?: string;
  updatedBy?: string;
  blockedBy?: string;
  metricsType?: number | string;
}

export const createFilterFunction = ({
  data,
  updateDate,
  deployDate,
  blockedDate,
  status,
  type,
  category,
  createdBy,
  updatedBy,
  blockedBy,
  metricsType,
}: ICreateFilterFunction) => {
  const versionsArray = data.versions ?? [data];

  return some(versionsArray, (version) => {
    let passFilter = true;

    // status filter
    if (status === EStatusStr.Undeployed) {
      passFilter = passFilter && isUndeployed(version);
    } else if (status === EStatusStr.Deployed) {
      passFilter = passFilter && isDeployed(version);
    }
    // type filter
    if (type === EBlockedEventType.BLOCK_ALL) {
      passFilter = passFilter && isBlockAll(version);
    } else if (type === EBlockedEventType.BLOCK_BY_RULES) {
      passFilter = passFilter && isBlockByRules(version);
    }

    // category filter
    if (category && category !== DEFAULT_SELECT_VALUE) {
      passFilter =
        passFilter && (version.category === category || version.event_category === category);
    }

    // metrics type filter
    if (metricsType !== undefined && metricsType !== DEFAULT_SELECT_VALUE) {
      passFilter = passFilter && version.metrics_type === metricsType;
    }

    // update date filter
    if (updateDate?.[0] && updateDate?.[1]) {
      if (!isNaN(version.updated_at)) {
        const time = moment(version.updated_at * 1000);
        passFilter = passFilter && time.isBetween(updateDate[0], updateDate[1]);
      }
    }
    // deploy date filter
    if (deployDate?.[0] && deployDate?.[1]) {
      if (version.deployed_at && !isNaN(version.deployed_at)) {
        const time = moment(version.deployed_at * 1000);
        passFilter = passFilter && time.isBetween(deployDate[0], deployDate[1]);
      } else {
        passFilter = false;
      }
    }
    // block date filter
    if (blockedDate?.[0] && blockedDate?.[1]) {
      if (version.block_timestamp && !isNaN(version.block_timestamp)) {
        const time = moment(version.block_timestamp * 1000);
        passFilter = passFilter && time.isBetween(blockedDate[0], blockedDate[1]);
      } else {
        passFilter = false;
      }
    }
    // created by filter
    if (createdBy && createdBy !== DEFAULT_SELECT_VALUE) {
      passFilter = passFilter && version.created_by === createdBy;
    }
    // updated by filter
    if (updatedBy && updatedBy !== DEFAULT_SELECT_VALUE) {
      passFilter = passFilter && version.updated_by === updatedBy;
    }
    // blocked by filter
    if (blockedBy && blockedBy !== DEFAULT_SELECT_VALUE) {
      passFilter = passFilter && version.block_by === blockedBy;
    }

    return passFilter;
  });
};

interface ICreateSearchFunction {
  data: { versions: any[] };
  searchText: string;
  supportSearchFields: string[];
  dateFields: string[];
}

export const createSearchFunction = ({
  data,
  searchText,
  supportSearchFields,
  dateFields,
}: ICreateSearchFunction) => {
  const versionsArray = data.versions ?? [data];

  return some(versionsArray ?? data, (version) => {
    let passFilter = true;
    // search filter
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      passFilter =
        passFilter &&
        some(supportSearchFields, (field) => {
          let fieldValue = get(version, field);

          if (dateFields.includes(field)) {
            fieldValue = formatTimestamp(fieldValue);
            return fieldValue && fieldValue.toLowerCase().includes(searchTextLower);
          } else if (field === 'status') {
            const formattedStatus = formatStatus(fieldValue) ?? '';
            return formattedStatus.toLowerCase().includes(searchTextLower);
          } else if (field === 'attribute_type') {
            const formattedStatus = formatAttributeType(fieldValue) ?? '';
            return formattedStatus.toLowerCase().includes(searchTextLower);
          } else if (field === 'mapped_event_names') {
            const mappedEventNames =
              Array.isArray(fieldValue) && fieldValue?.length > 0 ? fieldValue : ['--'];
            return mappedEventNames.some((item) => item.toLowerCase().includes(searchTextLower));
          } else if (
            field === 'related_metrics' ||
            field === 'related_event' ||
            field === 'associated_dimension_names' ||
            field === 'associated_flow_names'
          ) {
            const relatedMetricsText =
              Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue.toString() : '--';
            return relatedMetricsText.toLowerCase().includes(searchTextLower);
          } else {
            return fieldValue && fieldValue.toString().toLowerCase().includes(searchTextLower);
          }
        });
    }

    return passFilter;
  });
};

interface IGetFilteredSortedDataProps<T> {
  dataList: T[];
  filterParams: Omit<ICreateFilterFunction, 'data'>;
  searchParams: Omit<ICreateSearchFunction, 'data'>;
  sortParams: { key: string; order: ESortOrder };
}

export function getFilteredSortedData<T>({
  dataList = [],
  filterParams,
  searchParams,
  sortParams,
}: IGetFilteredSortedDataProps<T>) {
  // 1. Filter data
  const filterFunction = (item: any) => {
    return createFilterFunction({
      data: item,
      ...filterParams,
    });
  };

  const filterData = filter(cloneDeep(dataList), filterFunction);

  // 2. Search for data
  const searchFunction = (item: any) => {
    return createSearchFunction({
      data: item,
      ...searchParams,
    });
  };
  const searchData = filter(filterData, searchFunction);

  // 3. Sort the data
  let order: [sortByType.DESC | sortByType.ASC] | [] = [];
  if (sortParams.order === ESortOrder.DESC) {
    order = [sortByType.DESC];
  } else if (sortParams.order === ESortOrder.ASC) {
    order = [sortByType.ASC];
  }

  const sortedData =
    sortParams.key && sortParams.order !== 0
      ? orderBy(
          searchData ?? [],
          [(item) => get(item, `versions[0]${[sortParams.key]}`) ?? get(item, sortParams.key)],
          order,
        )
      : searchData;

  return sortedData;
}

// bulk action
export const isDeployAction = (actionType) => actionType === DEPLOYED;
export const isUndeployAction = (actionType) => actionType === UNDEPLOYED;
export const isDeleteAction = (actionType) => actionType === DELETED;

export const getNameKeyByResource = (resource: string) => {
  switch (resource) {
    case FLOW:
      return 'flow_name';
    case METRICS:
      return 'metric_name';
    case CUSTOM_DIMENSION:
      return 'display_name';
    case MAPPED_EVENTS:
    case BLOCK_EVENTS:
    case BLOCK_VIDEO_EVENTS:
      return 'event_name';
    default:
      return 'name';
  }
};

// const extractMainVersionIds = (data: any[], idKey: string): number[] => {
//   return chain(data)
//     .groupBy(idKey)
//     .map((group) => {
//       if (group.length === 1) {
//         return group[0].version_id;
//       }
//       const mainVersion = group.find((item) => item.is_draft === 0);
//       return mainVersion ? mainVersion.version_id : group[0].version_id;
//     })
//     .value();
// };

export const getIdsByResource = ({
  resource,
  data,
  actionType,
}: {
  resource: string;
  data: any[];
  actionType?: string | null;
}) => {
  let key = Id_Key_MAPPING[resource];
  if (isDeleteAction(actionType)) {
    key = 'version_id';
    return data.map((item) => item[key]);
  }
  return key ? data.map((item) => item[key] ?? item.id) : [];
};

export const getPluralSuffix = (count: number): string => {
  return count > 1 ? 's' : ' ';
};
